import { toast } from 'react-toastify';

import * as actions from './redux';
import {
  getWeb3,
  getAccounts,
  getNetworkId,
  getBlockNumber,
} from './web3Helpers';
import filterMyAuctions from './filterMyAuctions';
import * as contracts from './contracts';

function wrapAction(startAction, cb) {

  // Wrap thunk in function to make
  // actions interface consistent:
  return (...args) => {

    // Inject redux utils:
    return (dispatch, getState) => {

      // Notify starting action:
      dispatch(startAction());

      // Create async handler with dispatch:
      return new Promise((resolve, reject) => {
        cb({ dispatch, getState, resolve, reject }, ...args);
      });
    };
  }
}

function ethCall({ contract, methodName, eventName, showToast=true }) {
  return wrapAction(
    actions[`${eventName}Start`],
    ({ dispatch, getState, resolve, reject }, params=[], value='0') => {

      let web3 = getState().web3;

      // Create toast notification to track transaction:
      if (showToast) {
        var toastId = toast('Transaction processing...', {
          className: 'metamask',
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: false,
        });
      }

      value = web3.provider.utils.toWei(value, 'ether');

      contract.then((instance) => {

        // Check gas estimate for possible transaction errors:
        // THIS ERRORS ON SOME FUNCTIONS RE: INFURA GETH 1.7.2
        // return instance[methodName].estimateGas(...params, { value }).then((gas) => {});

        return instance[methodName](...params, {
          from: web3.accounts[0],
          value,
        });

      }).then((data) => {

        showToast && toast.update(toastId, {
          render: 'Success!',
          className: '',
          type: toast.TYPE.SUCCESS,
          autoClose: 5000,
        });

        dispatch(actions[`${eventName}Success`]({
          data
        }));
        resolve(data);

      }).catch((error) => {

        //TODO: pull these out into utility:
        if (error.message.match('240 seconds')) {

          showToast && toast.update(toastId, {
            render: 'Slow transaction. Check MetaMask.',
            className: '',
            type: toast.TYPE.WARN,
            autoClose: 10000,
          });

          dispatch(actions[`${eventName}Error`](error.message));
          reject(error.message);

        } else if (error.message.match('transaction: out of gas')) {

          showToast && toast.update(toastId, {
            render: 'Try again, increasing gas limit in MetaMask.',
            className: '',
            type: toast.TYPE.ERROR,
            autoClose: 10000,
          });

          dispatch(actions[`${eventName}Error`](error.message));
          reject(error.message);

        } else {

          showToast && toast.update(toastId, {
            render: 'Error!',
            className: '',
            type: toast.TYPE.ERROR,
            autoClose: 5000,
          });

          dispatch(actions[`${eventName}Error`](error.message));
          reject(error.message);

        }
      });
    }
  );
}

function feedbagCall({ path, method, eventName }) {
  return wrapAction(
    actions[`${eventName}Start`],
    ({ dispatch, getState, resolve, reject }, ...args) => {

      let url = getState().web3.feedbagUrl;

      return fetch(`${url}${path(...args)}`, { method }).then((resp) => {
        return resp.json();
      }).then((data) => {
        dispatch(actions[`${eventName}Success`](data));
        resolve(data);
      }).catch((error) => {
        dispatch(actions[`${eventName}Error`](error.message));
        reject(error.message);
      });

    }
  );
}

function ipfsIndexCall({ parseHash, eventName }) {
  return wrapAction(
    actions[`${eventName}Start`],
    ({ dispatch, getState, resolve, reject }, ...args) => {

      let url = getState().web3.feedbagUrl;

      fetch(`${url}/menu`).then((resp) => {
        return resp.json();
      }).then((menu) => {

        // Parse correct IPFS hash from menu object:
        let hash = parseHash(menu, ...args);

        if (!hash) {
          dispatch(actions[`${eventName}Success`]([]));
          return resolve([]);
        }

        let url = getState().web3.ipfsUrl;

        return fetch(`${url}/ipfs/${hash}`).then((resp) => {
          return resp.json();
        }).then((data) => {
          if (data.Message && data.Message.match(/Error/)) {
            throw new Error(data);
          }

          dispatch(actions[`${eventName}Success`](data));
          resolve(data);
        });

      }).catch((error) => {
        dispatch(actions[`${eventName}Error`](error.message));
        reject(error.message);
      });
    }
  );
}

function decentralizedURICall({ eventName }) {
  return wrapAction(
    actions[`${eventName}Start`],
    ({ dispatch, getState, resolve, reject }, ...args) => {

      let path = args[0];
      let hash = path.split('ipfs://ipfs/')[1];
      let url = getState().web3.ipfsUrl;

      if (!hash) {
        return reject('Invalid IPFS path:', path);
      }

      fetch(`${url}/ipfs/${hash}`).then((resp) => {
        return resp.json();
      }).then((data) => {

        if (data.Message && data.Message.match(/Error/)) {
          throw new Error(data);
        }

        dispatch(actions[`${eventName}Success`](path, data));
        resolve(data);
      }).catch((error) => {
        dispatch(actions[`${eventName}Error`](path, error.message));
        reject(error.message);
      });
    }
  );
}

/* ETHEREUM CALLS */

export const tokenGetName = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'name',
  eventName: 'tokenGetName',
  showToast: false,
});

export const tokenGetSymbol = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'symbol',
  eventName: 'tokenGetSymbol',
  showToast: false,
});

export const tokenGetMetadataPath = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'PLAYER_METADATA',
  eventName: 'tokenGetMetadataPath',
  showToast: false,
});

export const tokenWithdrawBalance = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'withdraw',
  eventName: 'tokenWithdrawBalance',
});

export const legendaryAuctionCreate = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'createLegendaryAuction',
  eventName: 'legendaryAuctionCreate',
});

export const rareAuctionCreate = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'createRareAuction',
  eventName: 'rareAuctionCreate',
});

export const commonAuctionCreate = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'createCommonAuction',
  eventName: 'commonAuctionCreate',
});

export const bidOnAuction = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'incrementBid',
  eventName: 'bidOnAuction',
});

export const auctionFinalize = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'finalizeAuction',
  eventName: 'auctionFinalize',
});

export const getUnclaimedBidsByAuctionIndexByBidder = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'unclaimedBidsByAuctionIndexByBidder',
  eventName: 'getUnclaimedBidsByAuctionIndexByBidder',
  showToast: false,
});

export const returnBids = ethCall({
  contract: contracts.AuctionableWorldCupPlayerTokenContract,
  methodName: 'returnBids',
  eventName: 'returnBids',
});

/* FEEDBAG CALLS */

export const reindexAuctions = feedbagCall({
  path: () => '/reindex/auctions',
  method: 'POST',
  eventName: 'reindexAuctions',
});

export const reindexLiveAuctions = feedbagCall({
  path: () => '/reindex/live-auctions',
  method: 'POST',
  eventName: 'reindexLiveAuctions',
});

const _unclaimedBidsGetAll = feedbagCall({
  path: (address) => `/unclaimed-bids/${address}`,
  method: 'GET',
  eventName: 'unclaimedBidsGetAll',
});
export const unclaimedBidsGetAll = () => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      getAccounts()
        .then(accounts => dispatch(_unclaimedBidsGetAll(accounts[0])))
        .then(resolve)
        .catch(reject);
    });
  };
}

// Polling events do not report analytics:
const _pollUnclaimedBidsGetAll = feedbagCall({
  path: (address) => `/unclaimed-bids/${address}`,
  method: 'GET',
  eventName: 'pollUnclaimedBidsGetAll',
});
export const pollUnclaimedBidsGetAll = () => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      getAccounts()
        .then(accounts => dispatch(_pollUnclaimedBidsGetAll(accounts[0])))
        .then(resolve)
        .catch(reject);
    });
  };
}

/* IPFS INDEX CALLS */

export const auctionsGetLive = ipfsIndexCall({
  parseHash: menu => menu['liveAuctions'],
  eventName: 'auctionsGetLive',
});

// Polling events do not report analytics:
export const pollAuctionsGetLive = ipfsIndexCall({
  parseHash: menu => menu['liveAuctions'],
  eventName: 'pollAuctionsGetLive',
});

export const auctionsGetAll = ipfsIndexCall({
  parseHash: menu => menu['auctions'],
  eventName: 'auctionsGetAll',
});

// Polling events do not report analytics:
export const pollAuctionsGetAll = ipfsIndexCall({
  parseHash: menu => menu['auctions'],
  eventName: 'pollAuctionsGetAll',
});

const _myTeamsGet = ipfsIndexCall({
  parseHash: (menu, address) => menu['myTeams'] && menu['myTeams'][address],
  eventName: 'myTeamsGet',
});
export const myTeamsGet = () => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      getAccounts()
        .then(accounts => dispatch(_myTeamsGet(accounts[0])))
        .then(resolve)
        .catch(err => {
          dispatch(actions.myTeamsGetError(err));
          reject(err);
        });
    });
  };
}

// Polling events do not report analytics:
const _pollMyTeamsGet = ipfsIndexCall({
  parseHash: (menu, address) => menu['myTeams'] && menu['myTeams'][address],
  eventName: 'pollMyTeamsGet',
});
export const pollMyTeamsGet = () => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      getAccounts()
        .then(accounts => dispatch(_pollMyTeamsGet(accounts[0])))
        .then(resolve)
        .catch(err => {
          dispatch(actions.pollMyTeamsGetError(err));
          reject(err);
        });
    });
  };
}

/* IPFS (DECENTRALIZED URIS: ipfs://ipfs/hashname) CALLS */

export const tokenGetMetadata = decentralizedURICall({
  eventName: 'tokenGetMetadata',
});

export const tokenGetMetadataManifest = decentralizedURICall({
  eventName: 'tokenGetMetadataManifest',
});

/* ASYNC LOAD + FILTERS */

export const myAuctionsGet = () => {
  return function (dispatch) {
    dispatch(actions.myAuctionsGetStart());
    return new Promise((resolve, reject) => {

      Promise.all([
        dispatch(auctionsGetAll()),
        dispatch(unclaimedBidsGetAll()),
        getBlockNumber(),
        getAccounts(),
      ]).then(deps => {

        let myAuctions = filterMyAuctions({
          auctions: deps[0],
          unclaimedBids: deps[1],
          blockNumber: deps[2],
          address: deps[3][0],
        });

        dispatch(actions.myAuctionsGetSuccess(myAuctions));
        resolve();
      }).catch((error) => {
        dispatch(actions.myAuctionsGetError(error));
        reject(error)
      });
    });
  }
}

//TODO: clean up polling duplication for reuse:
// Poller doesn't call analytics:
export const pollMyAuctionsGet = () => {
  return function (dispatch) {
    dispatch(actions.pollMyAuctionsGetStart());
    return new Promise((resolve, reject) => {

      Promise.all([
        dispatch(pollAuctionsGetAll()),
        dispatch(pollUnclaimedBidsGetAll()),
        getBlockNumber(),
        getAccounts(),
      ]).then(deps => {

        let myAuctions = filterMyAuctions({
          auctions: deps[0],
          unclaimedBids: deps[1],
          blockNumber: deps[2],
          address: deps[3][0],
        });

        dispatch(actions.pollMyAuctionsGetSuccess(myAuctions));
        resolve();
      }).catch((error) => {
        dispatch(actions.pollMyAuctionsGetError(error));
        reject(error)
      });
    });
  }
}


/* WEB3 */

export function loadWeb3(dispatch) {
  dispatch(actions.loadWeb3Start());
  return new Promise((resolve, reject) => {
    getWeb3()
      .then((web3) => {
        dispatch(actions.loadWeb3Success(web3));
        resolve(web3);
      })
      .catch((error) => {
        dispatch(actions.loadWeb3Error(error));
        reject(error)
      });
  });
}

export function updateBlockNumber(dispatch) {
  dispatch(actions.updateBlockNumberStart());
  return new Promise((resolve, reject) => {
    getBlockNumber().then(blockNumber => {
      dispatch(actions.updateBlockNumberSuccess(blockNumber));
      resolve(blockNumber);
    })
    .catch((error) => {
      dispatch(actions.updateBlockNumberError(error));
      reject(error)
    });
  });
}

export function loadWeb3Accounts(dispatch) {
  dispatch(actions.loadWeb3AccountsStart());
  return new Promise((resolve, reject) => {
    getAccounts()
      .then((accounts) => {
        dispatch(actions.loadWeb3AccountsSuccess(accounts));
        resolve(accounts);
      })
      .catch((error) => {
        dispatch(actions.loadWeb3AccountsError(error));
        reject(error)
      });
  });
}

export function loadNetworks(dispatch) {
  dispatch(actions.loadNetworksStart());
  return new Promise((resolve, reject) => {
    getNetworkId()
      .then((netId) => {
        dispatch(actions.loadNetworksSuccess(netId));
        resolve(netId);
      })
      .catch((error) => {
        dispatch(actions.loadNetworksError(error));
        reject(error)
      });
  });
}



// WEBPACK FOOTER //
// ./src/util/thunks.js